import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Space,
  Button,
  Row,
  Col,
  Typography,
  Carousel,
  theme,
  Card,
  Result,
  Spin,
  Tooltip,
} from 'antd';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SaveOutlined, UndoOutlined, WarningOutlined } from '@ant-design/icons';
import { InputField } from 'components/Forms';
import TextAreaField from 'components/Forms/textarea';
import DeliveryMap from './DeliveryMap';
import {
  Address,
  ShippingAddress,
  useAddressAlternatives,
  useLocateAddress,
  useSaveAddress,
  useShipmentAddress,
} from 'services/AddressesService';
import { CoreShipment } from 'types/packet';

const { Title, Paragraph } = Typography;

interface DeliveryAddressModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  shipment: CoreShipment;
}

const validationSchema = Yup.object().shape({
  address: Yup.object().shape({
    street: Yup.string().required('Campo obligatorio'),
    streetNumber: Yup.string().required('Campo obligatorio'),
    locality: Yup.string().required('Campo obligatorio'),
    postalCode: Yup.string().nullable(),
  }),
});

const DeliveryAddressModal: React.FC<DeliveryAddressModalProps> = ({
  visible,
  onClose,
  onSuccess,
  shipment,
}) => {
  const externalCode = shipment.externalCode || 'N/A';

  const { token } = theme.useToken();
  const formikRef = useRef<any>(null);
  const carouselRef = useRef<any>(null);
  const skipAlternativesEffect = useRef(false);
  const {
    data: shippingAddress,
    isError,
    isLoading,
    isFetching,
  } = useShipmentAddress(shipment.id, visible);
  const { data: { alternatives, ownerId } = {} } = useAddressAlternatives(
    shipment.id,
    visible,
  );
  const { mutateAsync: calculateAddress } = useLocateAddress(
    ownerId || shipment.ownerID,
  );
  const { mutateAsync: saveAddress } = useSaveAddress();
  const [selectedAddress, setSelectedAddress] = useState<Address | null>();
  const [selectedAlternative, setSelectedAlternative] = useState(0);
  const [mapAlternatives, setMapAlternatives] = useState<Address[]>(
    alternatives || [],
  );

  useEffect(() => {
    if (shippingAddress?.address) {
      setSelectedAddress({
        street: shippingAddress.address.street || '',
        streetNumber: shippingAddress.address.streetNumber || '',
        locality: shippingAddress.address.locality || '',
        postalCode: shippingAddress.address.postalCode || '',
      });
    }
  }, [shippingAddress]);

  useEffect(() => {
    if (alternatives && alternatives.length > 0) {
      skipAlternativesEffect.current = true;
      setMapAlternatives(alternatives);
    }
  }, [alternatives]);

  useEffect(() => {
    if (mapAlternatives && mapAlternatives.length > 0) {
      const alt = mapAlternatives[selectedAlternative];

      if (skipAlternativesEffect.current) {
        skipAlternativesEffect.current = false;
        return;
      }

      if (alt && formikRef.current) {
        setSelectedAddress({
          street: alt.street || '',
          streetNumber: alt.streetNumber || '',
          locality: alt.locality || '',
          postalCode: alt.postalCode || '',
        });
        formikRef.current.setValues({
          address: {
            ...formikRef.current.values.address,
            street: alt.street || '',
            streetNumber: alt.streetNumber || '',
            locality: alt.locality || '',
            postalCode: alt.postalCode || '',
            normalizedAddress: alt.normalizedAddress || '',
            lat: alt?.lat || 0,
            lng: alt?.lng || 0,
            exactMatch: alt.exactMatch || false,
            forcedFields: alt.forcedFields,
          },
          package: {
            ...formikRef.current.values.package,
            zone: {
              id: alt.zone?.id,
              name: alt.zone?.name,
              detail: alt.zone?.detail,
            },
          },
        });
      }
    }
  }, [selectedAlternative, mapAlternatives]);

  if (!shipment.id) {
    return null;
  }

  const onCarouselChange = (currentSlide: number) => {
    setSelectedAlternative(currentSlide);
  };

  const locateAddress = async (values: ShippingAddress) => {
    if (shipment.id) {
      const alts = await calculateAddress({
        street: values.address.street,
        number: values.address.streetNumber,
        postalCode: values.address.postalCode,
        city: values.address.locality,
      });
      if (alts && alts.length > 0) {
        setMapAlternatives(alts);
        setSelectedAlternative(0);
      }
    }
  };

  const handleClose = () => {
    setSelectedAddress(null);
    setSelectedAlternative(0);
    setMapAlternatives(alternatives || []);
    onClose();
  };

  const handleSubmit = async (values: ShippingAddress) => {
    if (!shipment.id) return;
    await saveAddress({ shipmentId: shipment.id, shippingAddress: values });
    handleClose();
    onSuccess();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', gap: 8 }}>
          <Title level={4} style={{ margin: 0 }}>
            Dirección de entrega:
          </Title>
          <Paragraph
            copyable
            style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              fontWeight: 'normal',
            }}
          >
            {externalCode}
          </Paragraph>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1000}
      destroyOnHidden
    >
      {isError ? (
        <Result
          status="404"
          title="Dirección no encontrada"
          extra={
            <Button type="primary" onClick={onClose}>
              Cerrar
            </Button>
          }
        />
      ) : (
        <Spin spinning={isLoading || isFetching}>
          <Formik
            innerRef={formikRef}
            initialValues={
              shippingAddress ?? {
                address: {},
                package: {},
              }
            }
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            validateOnMount
            enableReinitialize
          >
            {({
              isSubmitting,
              dirty,
              resetForm,
              values,
              initialValues,
              isValid,
            }) => {
              const addressDirty =
                values.address.street !== selectedAddress?.street ||
                values.address.streetNumber !== selectedAddress?.streetNumber ||
                values.address.locality !== selectedAddress?.locality ||
                values.address.postalCode !== selectedAddress?.postalCode;
              return (
                <Form>
                  <Row gutter={24}>
                    {/* Columna izquierda: Formulario */}
                    <Col
                      span={12}
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <div style={{ marginBottom: 16 }}>
                        <h4>{values.address.normalizedAddress}</h4>
                      </div>

                      {/* Campos de dirección estructurada */}
                      <Row gutter={16}>
                        <Col span={12}>
                          <InputField
                            label="Calle*"
                            fieldName="address.street"
                            placeholder="Nombre de la calle"
                          />
                        </Col>
                        <Col span={12}>
                          <InputField
                            label="Número*"
                            fieldName="address.streetNumber"
                            placeholder="Número de la dirección"
                          />
                        </Col>
                        <Col span={12}>
                          <InputField
                            label="Localidad*"
                            fieldName="address.locality"
                            placeholder="Localidad o distrito"
                          />
                        </Col>
                        <Col span={12}>
                          <InputField
                            label="Código Postal / Ubigeo"
                            fieldName="address.postalCode"
                            placeholder="Código postal"
                            status={
                              values.address.forcedFields?.includes(
                                'postalCode',
                              )
                                ? 'warning'
                                : undefined
                            }
                            suffix={
                              values.address.forcedFields?.includes(
                                'postalCode',
                              ) ? (
                                <Tooltip title="El geolocalizador no encontró un Codigo Postal / Ubigeo para la dirección. Manteniendo el ingresado.">
                                  <WarningOutlined
                                    style={{ color: '#faad14' }}
                                  />
                                </Tooltip>
                              ) : undefined
                            }
                          />
                        </Col>

                        <Col span={24} style={{ marginBottom: '16px' }}>
                          <label
                            style={{
                              display: 'block',
                              marginBottom: 4,
                            }}
                          >
                            Zona
                          </label>
                          <p
                            style={{
                              marginLeft: '12px',
                              color: values.package.zone?.id
                                ? 'inherit'
                                : 'red',
                            }}
                          >
                            {values.package.zone?.id
                              ? `${values.package.zone.name} - ${values.package.zone.detail}`
                              : 'Sin zonificar'}
                          </p>
                        </Col>

                        {/* TextAreas para comentarios */}
                        <div style={{ marginBottom: '16px', width: '100%' }}>
                          <TextAreaField
                            label="Información adicional de entrega"
                            fieldName="package.comment"
                            placeholder="Instrucciones especiales para la entrega..."
                            xs={24}
                          />
                        </div>
                        <TextAreaField
                          label="Información adicional de domicilio"
                          fieldName="package.shippingAddressComment"
                          placeholder="Referencias del domicilio..."
                          xs={24}
                        />
                      </Row>

                      {/* Botones de acción */}
                      <div style={{ marginTop: '16px', alignSelf: 'flex-end' }}>
                        <Space>
                          <Button onClick={onClose} disabled={isSubmitting}>
                            Cancelar
                          </Button>
                          {dirty && (
                            <Button
                              type="default"
                              icon={<UndoOutlined />}
                              onClick={() => {
                                skipAlternativesEffect.current = true;
                                resetForm({ values: initialValues });
                                setSelectedAddress({
                                  street: initialValues.address.street || '',
                                  streetNumber:
                                    initialValues.address.streetNumber || '',
                                  locality:
                                    initialValues.address.locality || '',
                                  postalCode:
                                    initialValues.address.postalCode || '',
                                });
                                setSelectedAlternative(0);
                                setMapAlternatives(alternatives || []);
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                          <Button
                            type={addressDirty ? 'primary' : 'default'}
                            disabled={!addressDirty || isSubmitting || !isValid}
                            onClick={() => locateAddress(values)}
                          >
                            Establecer ubicación
                          </Button>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={isSubmitting}
                            disabled={
                              !dirty || addressDirty || isSubmitting || !isValid
                            }
                          >
                            Guardar Cambios
                          </Button>
                        </Space>
                      </div>
                    </Col>

                    {/* Columna derecha: Mapa */}
                    <Col span={12}>
                      <div
                        style={{
                          height:
                            !values.address.exactMatch ||
                            (mapAlternatives && mapAlternatives.length > 1)
                              ? '85%'
                              : '100%',
                        }}
                      >
                        <DeliveryMap
                          selected={selectedAlternative}
                          alternatives={
                            mapAlternatives.length > 0
                              ? mapAlternatives
                              : shippingAddress?.address
                                ? [shippingAddress.address]
                                : []
                          }
                          setSelectedAlternative={(index) => {
                            setSelectedAlternative(index);
                            carouselRef.current.goTo(index);
                          }}
                        />
                      </div>
                      {mapAlternatives && mapAlternatives.length > 1 ? (
                        <Carousel
                          draggable
                          arrows
                          ref={carouselRef}
                          afterChange={onCarouselChange}
                          style={{ marginTop: '16px' }}
                        >
                          {mapAlternatives.map((alt, index) => (
                            <Card
                              key={index}
                              styles={{
                                body: {
                                  height: '10vh',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  lineHeight: '16px',
                                  color: '#fff',
                                  background: token.colorPrimary,
                                  display: 'flex',
                                  textAlign: 'center',
                                  textWrap: 'pretty',
                                  justifyContent: 'center',
                                  padding: '8px 20px 0 20px',
                                },
                              }}
                            >
                              <b>{alt.normalizedAddress}</b>
                            </Card>
                          ))}
                        </Carousel>
                      ) : (
                        !values.address.exactMatch && (
                          <h4
                            style={{ textAlign: 'center', marginTop: '26px' }}
                          >
                            La ubicación es poco precisa, es necesario más
                            información.
                          </h4>
                        )
                      )}
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </Spin>
      )}
    </Modal>
  );
};

export default DeliveryAddressModal;
